import { Core, TopShop } from "@faire/design-tokens";
import { BrandAvatar } from "@faire/retailer-visitor-shared/components/BrandAvatar/index";
import {
  ISuggestionEntry,
  ISuggestionEntryMixin,
} from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Interfaces";
import { InfoWrapper } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Styled";
import { useTopShopVisibility } from "@faire/retailer-visitor-shared/components/TopShop/useTopShopVisibility";
import { useEventsStore } from "@faire/retailer-visitor-shared/stores/domain/Events/shared/EventsStore";
import { getIcon } from "@faire/sdui/lib/getIcon";
import { IFaireIcon } from "@faire/web-api--source/indigofair/data/IFaireIcon";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { getSlateColorToken } from "@faire/web/common/colors/getSlateColorToken";
import { formatMoney } from "@faire/web/common/money";
import { Opacity } from "@faire/web/slate/Color";
import { TrophyIcon } from "@faire/web/slate/icons/Trophy";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { getEventTheme } from "@faire/web/slate/Theme/eventsThemeColors";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ThemeProvider } from "@faire/web/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import { TransparencyOverlay } from "@faire/web/ui/TransparencyOverlay";
import React, { useMemo } from "react";
import styled, { css } from "styled-components";

const OVERLAY_BACKGROUND_COLOR =
  "linear-gradient(0deg, rgba(0, 0, 0, 3%), rgba(0, 0, 0, 3%))";
const WRAPPER_HEIGHT = getSpacing("7x");
const WRAPPER_HEIGHT_SMALL = getSpacing("6x");

interface IProps {
  suggestion: ISuggestionEntryMixin;
  selected: boolean;
  isProduct: boolean;
  index: number;
  onHover: (selectedIdx: number) => void;
  onMouseLeave: () => void;
  onClick: (suggestion: ISuggestionEntry, navigate?: boolean) => void;
  shouldEnableSuggestionsImpressionAllocationV2?: boolean;
}

export const ResultDropdownImageItem: React.FC<IProps> = ({
  suggestion,
  selected,
  isProduct,
  index,
  onHover,
  onMouseLeave,
  onClick,
  shouldEnableSuggestionsImpressionAllocationV2,
}) => {
  const {
    profile_image,
    squared_image,
    name,
    image,
    is_highlighted_brand_for_promotional_event,
    wholesale_price,
    brand_name,
    profile_image_border_color,
    has_volume_pricing,
    subtitle,
    subtitle_icon,
  } = suggestion.searchData;
  const { nextOrActiveEventTheme } = useEventsStore(["nextOrActiveEventTheme"]);

  const onClickSuggestion = () => {
    onClick(suggestion, false);
  };

  const { isTopShopContentVisibleForBrand } = useTopShopVisibility(
    suggestion.searchData?.is_top_shop
  );

  const shouldShowAvatarBorder =
    isTopShopContentVisibleForBrand && !has_volume_pricing;

  const defaultSubtitle = (
    <>
      {isProduct ? (
        <PriceWrapper>
          {wholesale_price && (wholesale_price.amount_cents ?? 0) > 0
            ? getPriceMessage(wholesale_price, brand_name ?? "")
            : getPriceMessageNoPrice(brand_name ?? "")}
        </PriceWrapper>
      ) : (
        <BrandSubtitle
          subtitle={subtitle}
          subtitleIcon={subtitle_icon}
          isTopShopContentVisibleForBrand={isTopShopContentVisibleForBrand}
          hasVolumePricing={has_volume_pricing}
        />
      )}
    </>
  );

  const displayImage = isProduct ? image : (profile_image ?? squared_image);

  const borderColor: string | undefined = useMemo(() => {
    if (is_highlighted_brand_for_promotional_event) {
      return getEventTheme("faireMarket").decorative2 ?? undefined;
    }
    if (shouldShowAvatarBorder) {
      return profile_image_border_color
        ? getSlateColorToken(profile_image_border_color)
        : TopShop.shade.green;
    }
    return undefined;
  }, [
    is_highlighted_brand_for_promotional_event,
    shouldShowAvatarBorder,
    profile_image_border_color,
  ]);

  return (
    <li>
      <Wrapper
        $selected={selected}
        onMouseLeave={() => {
          onMouseLeave();
          onHover(-1);
        }}
        onMouseEnter={() => onHover(index)}
        to={suggestion.url}
        onClick={onClickSuggestion}
        tabIndex={-1}
        $height={
          shouldEnableSuggestionsImpressionAllocationV2
            ? WRAPPER_HEIGHT_SMALL
            : WRAPPER_HEIGHT
        }
      >
        <Row
          align="center"
          style={{ flex: 1, position: "relative", minWidth: 0 }}
        >
          <ThemeProvider theme={nextOrActiveEventTheme}>
            <BrandIconWrapper $isTopShop={shouldShowAvatarBorder}>
              <BrandIcon
                size="smaller"
                image={displayImage}
                isProduct={isProduct}
                showEventIcon={is_highlighted_brand_for_promotional_event}
                borderColor={borderColor}
              />
              {isProduct ? (
                <StyledOverlay color={OVERLAY_BACKGROUND_COLOR} />
              ) : null}
            </BrandIconWrapper>
          </ThemeProvider>
          <InfoWrapper>
            <NamePara>{name}</NamePara>
            {defaultSubtitle}
          </InfoWrapper>
        </Row>
      </Wrapper>
    </li>
  );
};

const getPriceMessage = (wholesale_price: IFaireMoney, brand_name: string) => {
  return (
    <StrictLocalMsg
      id="cBbr76"
      defaultMessage="{priceMoney} by {brandCompanyName}"
      values={{
        priceMoney: formatMoney({
          money: wholesale_price,
          omitCentsIfZero: true,
        }),
        brandCompanyName: brand_name,
      }}
      description={{
        text: "product costs {priceMoney} and is made by {brandCompanyName}",
      }}
    />
  );
};

const getPriceMessageNoPrice = (brand_name: string) => {
  return (
    <StrictLocalMsg
      id="mnvDDV"
      defaultMessage="by {brandCompanyName}"
      description={{
        text: "product is made by {brandCompanyName}",
      }}
      values={{
        brandCompanyName: brand_name,
      }}
    />
  );
};

const BrandSubtitle: React.FC<{
  subtitle?: string;
  subtitleIcon?: IFaireIcon;
  isTopShopContentVisibleForBrand?: boolean;
  hasVolumePricing?: boolean;
}> = ({
  subtitle,
  subtitleIcon,
  isTopShopContentVisibleForBrand,
  hasVolumePricing,
}) => {
  const icon = useMemo(() => {
    if (hasVolumePricing) {
      const IconComponent = getIcon(subtitleIcon);
      return subtitleIcon ? <IconComponent /> : undefined;
    }
    if (isTopShopContentVisibleForBrand) {
      return <StyledTrophyIcon active fontSize="medium" />;
    }
    return undefined;
  }, [subtitleIcon, isTopShopContentVisibleForBrand, hasVolumePricing]);

  return (
    <Row gap="1/2x" align="center">
      {icon}
      <PriceWrapper>
        {hasVolumePricing || isTopShopContentVisibleForBrand ? (
          <>
            {subtitle ?? (
              <StrictLocalMsg
                id="ORPA1Y"
                defaultMessage="Top Shop"
                description={{
                  text: "A short lint of text states that the brand is a Top Shop",
                  img: "https://cdn.faire.com/fastly/6d3f4ae894d54b4113823059d20879fe4676b2e7627bcaa296baab6fdca560de.png",
                }}
              />
            )}
          </>
        ) : null}
      </PriceWrapper>
    </Row>
  );
};

const StyledOverlay = styled(TransparencyOverlay)`
  border-radius: 2px;
`;

const Wrapper = styled(Link)<{
  $selected: boolean;
  $height: string;
}>`
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  height: ${({ $height }) => $height};
  background-color: ${({ $selected }) =>
    $selected ? Core.surface.subdued + Opacity.O_50 : null};
  &:focus {
    background-color: ${Core.surface.subdued + Opacity.O_50};
  }

  padding: ${getSpacing("1x", "2x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing(0, 0, 0, "2x")};
  }
`;

const BrandIcon = styled(BrandAvatar)<{ isProduct: boolean }>`
  border-radius: ${(props) => (props.isProduct ? "0" : "50%")};
  border: ${(props) => (props.isProduct ? "none" : undefined)};
`;

const BrandIconWrapper = styled.div<{ $isTopShop?: boolean }>`
  position: relative;
  ${({ $isTopShop }) =>
    $isTopShop
      ? css`
          margin-left: -3px;
        `
      : null}
`;

const PriceWrapper = styled(Typography).attrs({
  truncate: true,
  color: Core.text.subdued,
})`
  width: calc(100% - 20px);
`;

const NamePara = styled(Typography).attrs({
  truncate: true,
})`
  width: calc(100% - 20px);
`;

const StyledTrophyIcon = styled(TrophyIcon)`
  margin: -2px -2px 0 -3.5px;
`;
